// ReactQuillSafe.tsx
import React, { useRef, useEffect, useCallback } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import DOMPurify from 'dompurify';
import { message } from 'antd';

type Props = {
  value?: string;
  onChange?: (value: string) => void;
  readOnly?: boolean;
  placeholder?: string;
};

/**
 * ReactQuill controlled component that:
 *  - Sanitizes pasted HTML via DOMPurify (removes style, event handlers, scripts, etc.)
 *  - Strips <img> tags and data: URIs
 *  - Inserts sanitized HTML using Quill's clipboard (dangerouslyPasteHTML -> normalized)
 *  - Blocks pasted image blobs and drag-drop files
 */
const ReactQuillSafe: React.FC<Props> = ({
  value = '',
  onChange,
  readOnly = false,
  placeholder = '',
}) => {
  const quillRef = useRef<ReactQuill | null>(null);

  // call parent's onChange
  const handleChange = useCallback(
    (v: string) => {
      onChange?.(v);
    },
    [onChange]
  );

  // Strip <img> tags and inline data URIs from an HTML string
  const stripImagesAndDataUris = useCallback((html: string) => {
    if (!html) return html;
    // Remove <img ...> tags
    let cleaned = html.replace(/<img[^>]*>/gi, '');
    // Remove any leftover data:image URIs in inline styles or text
    cleaned = cleaned.replace(/data:image\/[^;]+;base64,[A-Za-z0-9+/=]+/gi, '');
    return cleaned;
  }, []);

  // DOMPurify configuration for a conservative allow-list
  const purify = useCallback((dirtyHtml: string) => {
    if (!dirtyHtml) return dirtyHtml;
    // Create a clean config: allow only safe tags/attributes you want
    return DOMPurify.sanitize(dirtyHtml, {
      ALLOWED_TAGS: [
        'p',
        'br',
        'ul',
        'ol',
        'li',
        'strong',
        'b',
        'em',
        'i',
        'u',
        'blockquote',
        'code',
        'pre',
        'a',
        'h1',
        'h2',
        'h3',
        'span',
        'div',
      ],
      // Only allow href/target/rel on anchors, strip style and on* attributes
      ALLOWED_ATTR: ['href', 'target', 'rel', 'class'],
      FORBID_ATTR: ['style', 'onerror', 'onload', 'onclick', 'srcdoc'],
      // Keep safe URL schemes for href (no javascript:)
      ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|ftp|tel):|[^a-z]|[-a-z+.]+(?:[^-a-z+.:]|$))/i,
    });
  }, []);

  // Insert sanitized HTML using Quill's clipboard insertion (safe & normalized)
  const insertSanitizedHtml = useCallback((html: string) => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;
    try {
      const range = quill.getSelection(true);
      const index = (range && range.index) || quill.getLength();
      // paste at index; Quill will parse/normalize the HTML into its Delta
      // Use 'user' source so it behaves like user paste
      (quill.clipboard as any).dangerouslyPasteHTML(index, html, 'user');
      // move cursor after insertion — Quill places caret after inserted content but ensure it:
      // (optional) quill.setSelection(index + html.length, 0);
    } catch (err) {
      console.error('insertSanitizedHtml error', err);
    }
  }, []);

  useEffect(() => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;

    const root = quill.root as HTMLElement;

    const handlePaste = (ev: ClipboardEvent) => {
      try {
        const clipboard = ev.clipboardData;
        if (!clipboard) return;

        // 1) Block image blobs in clipboard
        for (const item of Array.from(clipboard.items || [])) {
          if (item && item.type && item.type.startsWith('image/')) {
            ev.preventDefault();
            message.info('Pasting images is disabled.');
            return;
          }
        }

        // 2) If HTML present: strip images, purify, and insert via Quill
        const html = clipboard.getData?.('text/html');
        if (html) {
          // If HTML contains <img> tags or data URIs, sanitize them away first
          if (
            /<img[^>]*>/i.test(html) ||
            /data:image\/[^;]+;base64,/i.test(html) ||
            /style=/i.test(html)
          ) {
            ev.preventDefault();
            // remove images & data URIs
            const stripped = stripImagesAndDataUris(html);
            // run DOMPurify to remove styles/on* attributes and unsafe tags
            const cleaned = purify(stripped);
            if (cleaned && cleaned.trim().length > 0) {
              insertSanitizedHtml(cleaned);
            } else {
              message.info('Pasted content contained disallowed elements which were removed.');
            }
            return;
          }
          // else allow default paste (Quill will handle it)
          return;
        }

        // 3) Plain text: remove any embedded data: URIs before insertion
        const text = clipboard.getData?.('text/plain') || '';
        if (text && /data:image\/[^;]+;base64,/i.test(text)) {
          ev.preventDefault();
          const cleanedText = text.replace(/data:image\/[^;]+;base64,[A-Za-z0-9+/=]+/gi, '');
          const range = quill.getSelection(true);
          const index = (range && range.index) || quill.getLength();
          quill.insertText(index, cleanedText, 'user');
          quill.setSelection(index + cleanedText.length, 0);
          message.info('Removed inline images from pasted text.');
          return;
        }
      } catch (err) {
        console.error('paste handler error', err);
        // don't block other behavior if unexpected error occurs
      }
    };

    const handleDrop = (ev: DragEvent) => {
      try {
        ev.preventDefault();
        const dt = ev.dataTransfer;
        if (!dt) return;
        if (dt.files && dt.files.length > 0) {
          message.info('Dropping files or images is disabled.');
          return;
        }
      } catch (err) {
        console.error('drop handler error', err);
      }
    };

    root.addEventListener('paste', handlePaste);
    root.addEventListener('drop', handleDrop);
    root.addEventListener('dragover', (e) => e.preventDefault());

    return () => {
      root.removeEventListener('paste', handlePaste);
      root.removeEventListener('drop', handleDrop);
      root.removeEventListener('dragover', (e) => e.preventDefault());
    };
  }, [insertSanitizedHtml, purify, stripImagesAndDataUris]);

  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      ['blockquote', 'code-block'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['link'],
      [{ align: [] }],
      ['clean'],
    ],
  };

  const formats = [
    'header',
    'bold',
    'italic',
    'underline',
    'strike',
    'blockquote',
    'code-block',
    'list',
    'bullet',
    'link',
    'align',
  ];

  return (
    <ReactQuill
      ref={quillRef}
      theme="snow"
      value={value}
      onChange={handleChange}
      modules={modules}
      formats={formats}
      readOnly={readOnly}
      placeholder={placeholder}
    />
  );
};

export default ReactQuillSafe;
